import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { CapabilityRoleView } from '@/services/clubCapabilities'

type Props = {
  /** Manager name, shown as the modal subtitle. */
  name?: string
  roles: CapabilityRoleView[]
}

/**
 * Per-manager "View roles" affordance. Rendered only when a manager holds a capability role, so its
 * presence is the at-a-glance signal; clicking opens an information modal detailing each role — what
 * it grants, its human-readable permissions (when the server provides them), and seat usage. Built as
 * a scrollable list so it scales as more roles are introduced. Labels come from the server — nothing
 * here is hardcoded to a specific capability.
 */
export const ManagerRolesModal = ({ name, roles }: Props) => {
  const [open, setOpen] = useState(false)
  if (roles.length === 0) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded text-[11px] font-medium text-primary-600 hover:text-primary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        View roles
      </button>

      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 motion-reduce:transition-none" onClose={() => setOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="flex max-h-[85vh] w-full max-w-md transform flex-col overflow-hidden rounded-xl bg-white text-left align-middle shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
                    <div className="min-w-0">
                      <Dialog.Title as="h3" className="text-base font-bold text-gray-900">
                        Roles &amp; permissions
                      </Dialog.Title>
                      {name && <p className="mt-0.5 truncate text-xs text-gray-500">{name}</p>}
                    </div>
                    <button
                      onClick={() => setOpen(false)}
                      aria-label="Close"
                      className="-mr-1 -mt-1 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Body — one section per role; scrolls as roles grow */}
                  <div className="flex-1 overflow-y-auto p-5">
                    <ul className="flex flex-col gap-3">
                      {roles.map((role) => (
                        <li key={role.roleUrn} className="rounded-lg border border-gray-200 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-gray-900">{role.label}</p>
                            {role.seats && (
                              <span className="shrink-0 text-[11px] text-gray-500">
                                <span className="font-medium text-gray-900">{role.seats.remaining}</span> of{' '}
                                {role.seats.allocated} seats
                              </span>
                            )}
                          </div>

                          {role.capabilityLabel && (
                            <p className="mt-0.5 text-xs text-gray-500">Grants {role.capabilityLabel}</p>
                          )}
                          {role.description && (
                            <p className="mt-1 text-xs leading-snug text-gray-600">{role.description}</p>
                          )}

                          {role.permissions && role.permissions.length > 0 && (
                            <div className="mt-2">
                              <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                                Permissions
                              </p>
                              <ul className="flex flex-col gap-0.5">
                                {role.permissions.map((perm) => (
                                  <li key={perm} className="flex items-start gap-1.5 text-xs text-gray-600">
                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" aria-hidden />
                                    <span>{perm}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
